import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useConfirmIdentityMutation } from '@/shared/api/endpoints';
import { errorCode } from '@/shared/api/errors';
import { selectUser } from '@/shared/api/sessionSlice';
import { AskDialog } from '@/shared/ui/confirm';
import { Alert, TextField } from '@/shared/ui/primitives';

/** A change to other accounts, given the token that says it is the one asking. */
export type Confirmed = (token: string) => Promise<unknown>;

// The token of the latest confirmation, in this tab's memory only: never stored, so
// a closed tab or a reload asks again. A little before the server's end of it, it is
// let go, so that a change is not refused on its way.
let remembered: { token: string; until: number } | null = null;
const MARGIN_MS = 15_000;

/** Forget the confirmation (signing out, or the server no longer taking it). */
export function forgetConfirmation() {
  remembered = null;
}

/**
 * Before each change to another account the server wants the password typed again,
 * and the code of the second factor if the account has one. Asked once, it stands for
 * a few minutes: several changes in a row do not ask several times.
 *
 *   const [confirmed, dialog] = useConfirmation();
 *   <Button onClick={() => confirmed((token) => disable({ id, confirmed: token }).unwrap())} />
 *   {dialog}
 */
export function useConfirmation(): [run: (change: Confirmed) => void, dialog: ReactNode] {
  const { t } = useTranslation('access');
  const user = useSelector(selectUser);
  const [confirm, state] = useConfirmIdentityMutation();
  const [waiting, setWaiting] = useState<Confirmed | null>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const run = (change: Confirmed) => {
    if (remembered && remembered.until > Date.now()) {
      const token = remembered.token;
      void change(token).catch((error: unknown) => {
        // Ended on the server meanwhile: ask, and make the change then.
        if (errorCode(error) === 'password_required') {
          forgetConfirmation();
          setWaiting(() => change);
        }
      });
      return;
    }
    setWaiting(() => change);
  };

  const close = () => {
    setWaiting(null);
    setPassword('');
    setCode('');
    state.reset();
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    const answer = await confirm({ password, code: code || null });
    if (!answer.data) return;
    remembered = { token: answer.data.token, until: Date.now() + answer.data.expiresIn * 1000 - MARGIN_MS };
    const change = waiting;
    close();
    if (change) void change(answer.data.token).catch(() => undefined);
  };

  const failure = state.error ? (errorCode(state.error) ?? 'unknown') : null;
  const dialog = (
    <AskDialog
      open={waiting !== null}
      title={t('confirm.title')}
      confirmLabel={t('confirm.submit')}
      cancelLabel={t('common:dialog.cancel')}
      onConfirm={() => void submit()}
      onCancel={close}
    >
      <form className="space-y-3" onSubmit={(event) => void submit(event)}>
        <p className="text-sm text-muted">{t('confirm.why')}</p>
        <TextField
          label={t('confirm.password')}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
        {user?.secondFactor ? (
          <TextField
            label={t('confirm.code')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            required
          />
        ) : null}
        {failure ? (
          <Alert tone="bad" live>
            {t(`common:errors.${failure}`, { defaultValue: t('common:errors.unknown') })}
          </Alert>
        ) : null}
        {/* Enter in a field confirms, as the button does. */}
        <button type="submit" hidden />
      </form>
    </AskDialog>
  );
  return [run, dialog];
}
